import { useCallback, useEffect, useState } from 'react';
import { fetchUserProfile } from '../services/profileService';
import { normalizeProfile } from '../utils/profileFormat';

const explainError = (error) => {
    if (!error.response) return 'Cannot reach the server. Please check your connection and try again.';
    const { status, data } = error.response;
    if (status === 403) return 'You do not have permission to view this profile.';
    if (status === 404) return 'This profile could not be found.';
    if (status === 422) return 'This profile could not be opened.';
    return (typeof data?.detail === 'string' && data.detail) || 'The profile could not be loaded. Please try again.';
};

/**
 * Loads one user's profile from GET /rfp/auth/users/{user_id}.
 * @returns {{ profile: object|null, status: 'loading'|'loaded'|'error', error: string,
 *            reload: () => void, applyProfile: (rawProfile: object) => void }}
 *          applyProfile shows a profile the server just returned (e.g. after saving an edit)
 */
export const useProfile = (userId) => {
    const [profile, setProfile] = useState(null);
    const [status, setStatus] = useState('loading');
    const [error, setError] = useState('');
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        if (!userId) {
            setStatus('error');
            setError('Your account could not be identified. Please log in again.');
            return undefined;
        }

        let cancelled = false;
        setStatus('loading');
        setError('');

        fetchUserProfile(userId)
            .then((response) => {
                if (cancelled) return;
                setProfile(normalizeProfile(response.data));
                setStatus('loaded');
            })
            .catch((requestError) => {
                if (cancelled) return;
                setError(explainError(requestError));
                setStatus('error');
            });

        return () => {
            cancelled = true;
        };
    }, [userId, attempt]);

    const reload = useCallback(() => setAttempt((value) => value + 1), []);

    const applyProfile = useCallback((rawProfile) => {
        if (rawProfile && typeof rawProfile === 'object') {
            setProfile(normalizeProfile(rawProfile));
            setStatus('loaded');
        } else {
            setAttempt((value) => value + 1); // unexpected answer: load it again
        }
    }, []);

    return { profile, status, error, reload, applyProfile };
};
